import {
  COMPANY_NAME_AR,
  COMPANY_NAME_EN_REPLACEMENTS,
  COMPANY_NAME_PLACEHOLDER,
  COMPANY_NAME_WRONG_AR,
} from './companyName';
import { getManualArabic } from './manualAr';

const CACHE_KEY = 'qtech_translation_cache_v7';
const LEGACY_CACHE_KEYS = [
  'qtech_translation_cache_v1',
  'qtech_translation_cache_v2',
  'qtech_translation_cache_v3',
  'qtech_translation_cache_v4',
  'qtech_translation_cache_v5',
  'qtech_translation_cache_v6',
];
const MAX_TRANSLATE_LENGTH = 2000;

const SKIP_KEYS = new Set([
  'slug',
  'link',
  'to',
  'url',
  'documentId',
  'id',
  'publishedAt',
  'createdAt',
  'updatedAt',
  'coverImagePath',
  'imagePath',
  'iconPath',
  'listIconPath',
  'photoPath',
  'sliderImagePaths',
  'galleryPaths',
  'goalImagePath',
  'outcomeImagePath',
  'websiteUrl',
  'office_map_embed_url',
  'office_maps_share_url',
  'office_map_lat',
  'office_map_lng',
  'office_map_zoom',
  'facebook',
  'X',
  'instagram',
  'tiktok',
  'snapchat',
  'contact_phone',
  'contact_phone2',
  'contact_email',
  'hr_email',
  'office_hours',
  'top_header_address',
  'address',
  'office_address_line1',
  'office_address_line2',
  'office_map_query',
  'filters',
  'sortOrder',
  'featured',
  'category',
  'counter1Value',
  'counter2Value',
  'key',
]);

let currentLocale = localStorage.getItem('qtech_locale') || 'en';

const applyCompanyNameReplacements = (text, pairs) => {
  let result = text;
  pairs.forEach(([from, to]) => {
    if (from && to && result.includes(from)) {
      result = result.split(from).join(to);
    }
  });
  return result;
};

const dedupeArabicCompanyName = (text) => {
  if (!text || typeof text !== 'string') return text;

  let result = text;

  // Repair stacked duplicates from earlier replacement bug
  result = result.replace(/(?:شركة\s+){2,}/g, 'شركة ');
  result = result.replace(/(?:للمقاولات\s*){2,}/g, 'للمقاولات ');
  result = result.replace(
    /(?:شركة\s+)*تقنيات الجودة(?:\s+للمقاولات)+/g,
    COMPANY_NAME_AR,
  );
  result = result.replace(
    /(?:شركة\s+)+تقنيات الجودة(?:\s+للمقاولات)?/g,
    COMPANY_NAME_AR,
  );

  return result.replace(/\s{2,}/g, ' ').trim();
};

const restoreCompanyName = (text) => {
  if (!text || typeof text !== 'string') return text;

  let result = text.split(COMPANY_NAME_PLACEHOLDER).join(COMPANY_NAME_AR);
  result = dedupeArabicCompanyName(result);

  if (result.includes(COMPANY_NAME_AR)) {
    return dedupeArabicCompanyName(result);
  }

  COMPANY_NAME_WRONG_AR.forEach((wrong) => {
    if (result.includes(wrong)) {
      result = result.split(wrong).join(COMPANY_NAME_AR);
    }
  });

  return dedupeArabicCompanyName(result);
};

const prepareEnglishForTranslation = (text) => {
  if (!text || text.includes(COMPANY_NAME_AR)) {
    return dedupeArabicCompanyName(text);
  }
  return applyCompanyNameReplacements(text, COMPANY_NAME_EN_REPLACEMENTS);
};

const isInvalidTranslation = (original, candidate) => {
  if (!candidate || typeof candidate !== 'string') return true;
  if (candidate === original) return false;

  const upper = candidate.toUpperCase();
  if (upper.includes('MYMEMORY WARNING')) return true;
  if (upper.includes('USAGELIMITS')) return true;
  if (upper.includes('NEXT AVAILABLE IN')) return true;
  if (upper.includes('TRANSLATE MORE')) return true;
  if (upper.includes('QUOTA')) return true;
  if (candidate.length > 300 && candidate.length > (original?.length || 0) * 2) return true;

  return false;
};

const sanitizeCache = (cache) => {
  const clean = {};
  Object.entries(cache || {}).forEach(([key, value]) => {
    const original = key.split('|').slice(2).join('|');
    const normalized = typeof value === 'string' ? restoreCompanyName(value) : value;
    if (!isInvalidTranslation(original, normalized)) {
      clean[key] = normalized;
    }
  });
  return clean;
};

const readCache = () => {
  try {
    LEGACY_CACHE_KEYS.forEach((key) => localStorage.removeItem(key));
    return sanitizeCache(JSON.parse(localStorage.getItem(CACHE_KEY) || '{}'));
  } catch {
    return {};
  }
};

let memoryCache = readCache();

const persistCache = () => {
  localStorage.setItem(CACHE_KEY, JSON.stringify(memoryCache));
};

export const purgeBadTranslationCache = () => {
  LEGACY_CACHE_KEYS.forEach((key) => localStorage.removeItem(key));
  memoryCache = sanitizeCache(memoryCache);
  persistCache();
};

export const getLocale = () => currentLocale;

export const setLocale = (locale) => {
  currentLocale = locale === 'ar' ? 'ar' : 'en';
  localStorage.setItem('qtech_locale', currentLocale);
};

const shouldSkipString = (value, key) => {
  if (!value || typeof value !== 'string') return true;
  if (SKIP_KEYS.has(key)) return true;
  if (value.length > MAX_TRANSLATE_LENGTH) return true;
  if (/^https?:\/\//i.test(value)) return true;
  if (/^[\w.-]+@/.test(value)) return true;
  if (/^\+?\d[\d\s-]{6,}$/.test(value.trim())) return true;
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return true;
  if (/^\/[\w/-]*$/.test(value.trim())) return true;
  return false;
};

async function translateViaGoogle(text, target = 'ar') {
  const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${target}&dt=t&q=${encodeURIComponent(text)}`;
  const response = await fetch(url);
  if (!response.ok) return null;

  const json = await response.json();
  const parts = Array.isArray(json?.[0]) ? json[0] : [];
  const translated = parts.map((part) => part?.[0]).join('').trim();

  return translated || null;
}

export async function translateText(text, target = 'ar') {
  if (!text || typeof text !== 'string') return text;
  if (target === 'en') return text;

  const trimmed = text.trim();
  if (!trimmed || shouldSkipString(trimmed, '')) return text;

  // Skip if text already contains Arabic characters
  if (/[\u0600-\u06FF]/.test(trimmed)) return trimmed;

  const manual = getManualArabic(trimmed);
  if (manual) return manual;

  const prepared = prepareEnglishForTranslation(trimmed);

  if (prepared === COMPANY_NAME_PLACEHOLDER) return COMPANY_NAME_AR;

  const cacheKey = `en|${target}|${trimmed}`;
  const cached = memoryCache[cacheKey];
  if (cached && !isInvalidTranslation(trimmed, cached)) {
    return restoreCompanyName(cached);
  }

  try {
    const translated = await translateViaGoogle(prepared, target);

    if (!translated || isInvalidTranslation(trimmed, translated)) {
      return restoreCompanyName(prepared);
    }

    const normalized = restoreCompanyName(translated);
    memoryCache[cacheKey] = normalized;
    persistCache();
    return normalized;
  } catch {
    return restoreCompanyName(prepared);
  }
}

export async function translateDeep(value, key = '') {
  if (value == null) return value;

  if (typeof value === 'string') {
    if (getLocale() === 'en' || shouldSkipString(value, key)) return value;
    return translateText(value, 'ar');
  }

  if (Array.isArray(value)) {
    return Promise.all(value.map((item) => translateDeep(item, key)));
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    const translatedEntries = await Promise.all(
      entries.map(async ([entryKey, entryValue]) => {
        const translatedValue = await translateDeep(entryValue, entryKey);
        return [entryKey, translatedValue];
      })
    );
    return Object.fromEntries(translatedEntries);
  }

  return value;
}

export const localizeIfNeeded = async (value) => {
  if (getLocale() === 'en' || value == null) return value;
  return translateDeep(value);
};

export const getCachedTranslation = (text, target = 'ar') => {
  if (!text || target === 'en') return text;
  const trimmed = text.trim();
  if (/[\u0600-\u06FF]/.test(trimmed)) return trimmed;
  const manual = getManualArabic(trimmed);
  if (manual) return manual;
  const cacheKey = `en|${target}|${trimmed}`;
  const cached = memoryCache[cacheKey];
  if (cached && !isInvalidTranslation(text.trim(), cached)) {
    return restoreCompanyName(cached);
  }
  return restoreCompanyName(prepareEnglishForTranslation(text.trim()));
};

purgeBadTranslationCache();

